import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { 
  Home, 
  Search, 
  FolderOpen, 
  Shirt, 
  User, 
  Plus, 
  Activity, 
  Heart, 
  Settings, 
  HelpCircle,
  Package,
  Layers,
  ChevronDown,
  ChevronUp,
  ArrowLeftRight,
  Shield
} from 'lucide-react';
import Logo3D from './Logo3D';

const Sidebar = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);

  const navItems = [
    { path: '/', icon: Home, label: 'Home' },
    { path: '/explore', icon: Search, label: 'Explore' },
    { path: '/capsules', icon: Layers, label: 'Capsules' },
    { path: '/activity', icon: Activity, label: 'Activity' },
    { path: '/settings', icon: Settings, label: 'Settings' },
    { path: '/help', icon: HelpCircle, label: 'Help' },
    { path: '/verification', icon: Shield, label: 'Verification' },
  ];


  const profileSubItems = [
    { path: '/outfit-builder', label: 'Wardrobe' },
    { path: '/portfolio', label: 'Portfolio' },
    { path: '/created', label: 'Created' },
    { path: '/listings', label: 'Listings' },
    { path: '/favorites', label: 'Favorites' },
    { path: '/offers', label: 'Offers' },
    { path: '/outfits', label: 'Outfits' },
    { path: '/trade-pieces', label: 'Trade Pieces' },
    { path: '/create-nft', label: 'Create Piece' },
    { path: '/create-collection', label: 'Create Capsule' },
  ];

  const shouldShowLabels = isExpanded || isHovered;

  return (
    <div
      style={{ backgroundColor: '#000000' }}
      className={`sidebar bg-black border-r border-[#111111] flex-col transition-all duration-300 ease-in-out overflow-hidden hidden md:flex ${
        shouldShowLabels ? 'w-64' : 'w-16'
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
                    {/* Navigation */}
        <nav className="flex-1 p-2 space-y-1 overflow-y-auto scrollbar-thin bg-black">
                     {/* Logo as clickable home button */}
                       <NavLink
              to="/"
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-white transition-all duration-200 cursor-pointer group hover:scale-105"
            >
                             <div className="flex-shrink-0 flex items-center justify-center" style={{ width: '24px', height: '24px' }}>
                                                      <Logo3D 
                     className="w-full h-full"
                     autoRotate={true}
                     baseRotationSpeed={0.02}
                     enableControls={false}
                     showEnvironment={true}
                     cameraPosition={[0, 0, 55]}
                     fov={30}
                     heroAreaMode={true}
                     loadingMode={false}
                   />
               </div>
                           <span className={`font-medium text-sm transition-all duration-300 ${
                shouldShowLabels ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
              }`}>
                Outsera
              </span>
           </NavLink>
        {navItems.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-white transition-all duration-200 cursor-pointer group hover:scale-105 ${
                  isActive || item.active ? 'bg-black-800/50' : ''
                }`
              }
              style={({ isActive }) => ({
                backgroundColor: isActive || item.active ? '#2a2a2a' : 'transparent'
              })}
              onMouseEnter={(e) => {
                if (!e.currentTarget.classList.contains('active')) {
                  e.currentTarget.style.backgroundColor = '#2a2a2a';
                }
              }}
              onMouseLeave={(e) => {
                if (!e.currentTarget.classList.contains('active')) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              <div className="flex-shrink-0 transition-transform duration-200 group-hover:scale-110">
                <Icon size={18} />
              </div>
              <span className={`font-medium text-sm transition-all duration-300 ${
                shouldShowLabels ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
              }`}>
                {item.label}
              </span>
            </NavLink>
          );
        })}


        {/* Profile Section */}
        <div className="mt-4">
          {/* Main Profile Button */}
          <NavLink
            to="/profile"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-white transition-all duration-200 cursor-pointer group hover:scale-105 ${
                isActive ? 'bg-black-800/50' : ''
              }`
            }
            style={({ isActive }) => ({
              backgroundColor: isActive ? '#2a2a2a' : 'transparent'
            })}
            onMouseEnter={(e) => {
              if (!e.currentTarget.classList.contains('active')) {
                e.currentTarget.style.backgroundColor = '#2a2a2a';
              }
            }}
            onMouseLeave={(e) => {
              if (!e.currentTarget.classList.contains('active')) {
                e.currentTarget.style.backgroundColor = 'transparent';
              }
            }}
          >
            <div className="flex-shrink-0 transition-transform duration-200 group-hover:scale-110">
              <User size={18} />
            </div>
            <span className={`font-medium text-sm transition-all duration-300 ${
              shouldShowLabels ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
            }`}>
              Profile
            </span>
          </NavLink>

          {/* Profile Dropdown for Quick Access */}
          <button
            onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
            className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-white transition-all duration-200 cursor-pointer group hover:scale-105 ${
              profileDropdownOpen ? 'bg-black-800/50' : ''
            }`}
            style={{
              backgroundColor: profileDropdownOpen ? '#2a2a2a' : 'transparent'
            }}
            onMouseEnter={(e) => {
              if (!profileDropdownOpen) {
                e.currentTarget.style.backgroundColor = '#2a2a2a';
              }
            }}
            onMouseLeave={(e) => {
              if (!profileDropdownOpen) {
                e.currentTarget.style.backgroundColor = 'transparent';
              }
            }}
          >
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 transition-transform duration-200 group-hover:scale-110">
                <Package size={18} />
              </div>
              <span className={`font-medium text-sm transition-all duration-300 ${
                shouldShowLabels ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
              }`}>
                Quick Access
              </span>
            </div>
            <div className={`transition-all duration-300 ${
              shouldShowLabels ? 'opacity-100' : 'opacity-0'
            }`}>
              {profileDropdownOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </div>
          </button>

          {/* Profile Sub-items */}
          {profileDropdownOpen && (
            <div className="mt-2 ml-4 space-y-1">
              {profileSubItems.map((subItem) => (
                <NavLink
                  key={subItem.path}
                  to={subItem.path}
                  className={({ isActive }) =>
                    `block px-3 py-2 rounded-lg text-sm text-white transition-all duration-200 cursor-pointer hover:scale-105 ${
                      isActive ? 'bg-black-800/50' : ''
                    }`
                  }
                  style={({ isActive }) => ({
                    backgroundColor: isActive ? '#2a2a2a' : 'transparent'
                  })}
                  onMouseEnter={(e) => {
                    if (!e.currentTarget.classList.contains('active')) {
                      e.currentTarget.style.backgroundColor = '#2a2a2a';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!e.currentTarget.classList.contains('active')) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <span className={`transition-all duration-300 ${
                    shouldShowLabels ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
                  }`}>
                    {subItem.label}
                  </span>
                </NavLink>
              ))}
            </div>
          )}
        </div>
      </nav>

    </div>
  );
};

export default Sidebar; 